// src/pages/DashboardPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  collectionGroup,
  limit
} from "firebase/firestore";
import "./Dashboard.css";
import "./EvolutionThread.css";
import BottomNav from "./BottomNav";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuthor, authorAvatarUrl } from "../utils/authorLookup";
import { getImageUrl, getThumbUrl } from "../utils/imageUrls";

// Assets
import OrbitIcon from "../assets/orbit.png";
import NovaIcon from "../assets/nova.png";

// Hero background. It now loads through a stable public URL.
// TIP: for zero Supabase egress, put the file in your app's /public folder
// (e.g. public/dashboard-hero.jpg) and set BG_LOCAL_URL = "/dashboard-hero.jpg".
const BG_IMAGE_PATH = "app/backgrounds/dashboard-hero.jpg";
const BG_LOCAL_URL = "";

const TAB_CACHE_TTL = 5 * 60 * 1000; // results of a tab are reused for 5 min while this page stays open

const auth = getAuth();
const db = getFirestore();

// Helper: Format Time
function formatTimeAgo(date) {
  if (!date?.toDate) return "Recently";
  const d = date.toDate();
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

// Name + avatar of any user (shared lookup: cached, never uses email)
async function fetchUserProfile(userId) {
  if (!userId) return { name: "Unknown", image: null };
  const author = await getAuthor(userId);
  if (!author) return { name: "Unknown", image: null };
  return { name: author.name || "User", image: authorAvatarUrl(author) };
}

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [uid, setUid] = useState("");

  // Tabs: "rewarded" (competition) | "public"
  const initialTab = location.state?.activeTab === "competition"
    ? "rewarded"
    : (location.state?.activeTab || "public");
  const [activeTab, setActiveTab] = useState(initialTab);

  // SubTabs: "all" | "impressed" | "evolved"
  const [subTab, setSubTab] = useState("all");

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);

  // Remembers each tab's results while this page is open, so switching tabs
  // back and forth does not refetch. Resets when you leave the page, so a
  // freshly posted idea always shows up on your next visit.
  const tabCache = useRef(new Map());

  const bgUrl = BG_LOCAL_URL || getImageUrl(BG_IMAGE_PATH) || "";

  // Auth Listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || "");
    });
    return unsub;
  }, []);

  // Own name + avatar, needed for the "Evolved" tab
  useEffect(() => {
    if (!uid || subTab !== "evolved") return undefined;
    let mounted = true;
    fetchUserProfile(uid).then((p) => {
      if (mounted) setMyProfile(p);
    });
    return () => { mounted = false; };
  }, [uid, subTab]);

  // Fetch Logic
  useEffect(() => {
    if (!uid) return undefined;
    let isMounted = true;

    const cacheKey = `${activeTab}|${subTab}`;
    const cached = tabCache.current.get(cacheKey);
    if (cached && Date.now() - cached.at < TAB_CACHE_TTL) {
      setIdeas(cached.ideas);
      setLoading(false);
      return () => { isMounted = false; };
    }

    const fetchDashboardContent = async () => {
      setLoading(true);
      try {
        // 1. Define Queries
        const isCompetition = activeTab === "rewarded";
        // Support all collections
        const PUBLIC_COLLS = ["postedIdeas", "publicIdeas"];
        const COMP_COLLS = ["competitionIdeas", "PostedCompetitionIdeas"];
        const targetCollections = isCompetition ? COMP_COLLS : PUBLIC_COLLS;

        // --- FETCH IMPRESSED (My Ideas) ---
        const fetchImpressed = async (colls) => {
          let results = [];
          const seen = new Set();
          for (const coll of colls) {
            try {
              const isComp = coll === "competitionIdeas" || coll === "PostedCompetitionIdeas";
              const q1 = isComp ? query(collection(db, coll), where("ownerId", "==", uid), where("status", "==", "paid")) : query(collection(db, coll), where("ownerId", "==", uid));
              const q2 = isComp ? query(collection(db, coll), where("userId", "==", uid), where("status", "==", "paid")) : query(collection(db, coll), where("userId", "==", uid));
              const q3 = isComp ? query(collection(db, coll), where("uid", "==", uid), where("status", "==", "paid")) : query(collection(db, coll), where("uid", "==", uid));
              const snapshots = await Promise.all([getDocs(q1), getDocs(q2), getDocs(q3)]);
              snapshots.forEach(snap => {
                snap.docs.forEach(d => {
                  if (seen.has(d.id)) return;
                  seen.add(d.id);
                  results.push({ ...d.data(), id: d.id, _type: 'impressed', _actionTime: d.data().createdAt, _sourceColl: coll });
                });
              });
            } catch (e) { console.warn(`Error fetching impressed from ${coll}:`, e); }
          }
          return results;
        };

        // --- FETCH EVOLVED (Ideas I evolved) ---
        const fetchEvolved = async (colls) => {
          try {
            const q1 = query(collectionGroup(db, 'evolves'), where('userId', '==', uid));
            const q2 = query(collectionGroup(db, 'evolves'), where('uid', '==', uid));
            const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
            const allEvolveDocs = [...snap1.docs, ...snap2.docs];

            const ideaMap = {}; // ideaId -> { latestTime, ideaRef }
            allEvolveDocs.forEach(evolveDoc => {
              const ideaRef = evolveDoc.ref.parent.parent;
              if (!ideaRef) return;
              const isTarget = colls.includes(ideaRef.parent.id);
              if (isTarget) {
                const t = evolveDoc.data().createdAt;
                if (!ideaMap[ideaRef.id]) ideaMap[ideaRef.id] = { time: t, ref: ideaRef, text: evolveDoc.data().text, stage: evolveDoc.data().stage };
                else if (t && ideaMap[ideaRef.id].time && t.toMillis() > ideaMap[ideaRef.id].time.toMillis()) {
                  ideaMap[ideaRef.id] = { time: t, ref: ideaRef, text: evolveDoc.data().text, stage: evolveDoc.data().stage };
                }
              }
            });

            const evolvedIdeas = await Promise.all(Object.keys(ideaMap).map(async (id) => {
              const { ref, time } = ideaMap[id];
              const snap = await getDoc(ref);
              if (snap.exists()) {
                const data = snap.data();
                const owner = data.ownerId || data.userId || data.uid;
                if (owner === uid) return null; // Skip if it's my own idea
                return {
                  ...data,
                  id: snap.id,
                  _type: 'evolved',
                  _actionTime: time,
                  _sourceColl: ref.parent.id,
                  _myEvolveText: ideaMap[id].text,
                  _myEvolveStage: ideaMap[id].stage
                };
              }
              return null;
            }));
            return evolvedIdeas.filter(Boolean);
          } catch (e) {
            console.warn("Evolved query error (possibly missing index):", e);
            return [];
          }
        };

        // --- HELPER FOR EVOLUTION DETAILS ---
        const fetchLatestEvolution = async (ideaId, collName) => {
          try {
            const q = query(
              collection(db, collName, ideaId, "evolves"),
              orderBy("createdAt", "desc"),
              limit(1)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const docData = snap.docs[0].data();
              return { ...docData, id: snap.docs[0].id };
            }
          } catch (e) {
            console.error("Error fetching evolution:", e);
          }
          return null;
        };

        // --- EXECUTE ---
        let finalDocs = [];

        if (subTab === 'all') {
          finalDocs = await fetchImpressed(targetCollections);
        } else if (subTab === 'impressed') {
          const allMyPosts = await fetchImpressed(targetCollections);
          const filtered = allMyPosts.filter(idea => (idea.evolveCount || 0) > 0);
          finalDocs = await Promise.all(filtered.map(async (idea) => {
            const actualColl = idea._sourceColl || targetCollections[0];
            const latestEvolve = await fetchLatestEvolution(idea.id, actualColl);
            if (latestEvolve) {
              const evolverProfile = await fetchUserProfile(latestEvolve.userId || latestEvolve.uid);
              return {
                ...idea,
                latestEvolution: {
                  text: latestEvolve.text,
                  stage: latestEvolve.stage || 'Spark', // Fallback
                  evolverName: evolverProfile.name,
                  evolverImage: evolverProfile.image,
                  userId: latestEvolve.userId || latestEvolve.uid
                }
              };
            }
            return idea;
          }));
        } else if (subTab === 'evolved') {
          finalDocs = await fetchEvolved(targetCollections);
        }

        // --- SORT ---
        finalDocs.sort((a, b) => {
          const tA = a._actionTime?.toMillis ? a._actionTime.toMillis() : 0;
          const tB = b._actionTime?.toMillis ? b._actionTime.toMillis() : 0;
          return tB - tA;
        });

        // --- ADD IMAGE URLS (public, instant: no network requests) ---
        const hydrated = finalDocs.map((item) => {
          const rawPath = item.imagePath || item.image;

          // Label
          let label = "Posted";
          if (item._type === 'evolved') label = "Evolved";
          else if (activeTab === 'rewarded') label = "Competed";

          return {
            ...item,
            imageUrl: getImageUrl(rawPath) || "",   // full size (detail page)
            thumbUrl: getThumbUrl(rawPath) || "",   // small (cards here)
            timeAgo: formatTimeAgo(item._actionTime),
            displayAction: label
          };
        });

        tabCache.current.set(cacheKey, { at: Date.now(), ideas: hydrated });
        if (isMounted) setIdeas(hydrated);

      } catch (err) {
        console.error("Dashboard Global Error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboardContent();
    return () => { isMounted = false; };
  }, [uid, activeTab, subTab]); // Refetch when filters change

  // Loading Skeleton
  if (loading) return (
    <div className={`dashboard__container ${bgUrl ? "dashboard__has-bg" : ""}`} data-tab={activeTab} style={bgUrl ? { backgroundImage: `url("${bgUrl}")` } : undefined}>
      {/* Header Skeleton */}
      <div className="dashboard__header">

        <h1 className="dashboard__title">Dashboard</h1>
      </div>


      <div className="dashboard__tabs">
        <button className={`tab ${activeTab === "rewarded" ? "tab--active" : ""}`}>Competition</button>
        <button className={`tab ${activeTab === "public" ? "tab--active" : ""}`}>Public Posts</button>
      </div>

      <div className="ideas-grid">
        <IdeaSkeleton />
        <IdeaSkeleton />
        <IdeaSkeleton />
      </div>
      <BottomNav />
    </div>
  );

  return (
    <div className={`dashboard__container ${bgUrl ? "dashboard__has-bg" : ""}`} data-tab={activeTab} style={bgUrl ? { backgroundImage: `url("${bgUrl}")` } : undefined}>

      {/* Header */}
      <div className="dashboard__header">

        <h1 className="dashboard__title">Dashboard</h1>
      </div>



      {/* Main Tabs (Competition vs Public) */}
      <div className="dashboard__tabs">
        <button
          className={`tab ${activeTab === "rewarded" ? "tab--active" : ""}`}
          onClick={() => {
            setActiveTab("rewarded");
            setSubTab("all"); // Reset subTab when switching to competition
          }}
        >
          Competition
        </button>
        <button className={`tab ${activeTab === "public" ? "tab--active" : ""}`} onClick={() => setActiveTab("public")}>Public Posts</button>
      </div>

      {/* Sub Tabs (All | Impressed | Evolved) */}
      <div className="dashboard__subtabs">
        <button className={`subtab ${subTab === "all" ? "subtab--active" : ""}`} onClick={() => setSubTab("all")}>All</button>
        {activeTab !== "rewarded" && (
          <>
            <button className={`subtab ${subTab === "impressed" ? "subtab--active" : ""}`} onClick={() => setSubTab("impressed")}>Impressed</button>
            <button className={`subtab ${subTab === "evolved" ? "subtab--active" : ""}`} onClick={() => setSubTab("evolved")}>Evolved</button>
          </>
        )}
      </div>

      {/* Grid */}
      <div className="ideas-grid">
        {ideas.length === 0 ? (
          <div className="empty-state">No ideas found.</div>
        ) : (
          ideas.map(idea => {
            // CHECK IF THIS IS AN IMPRESSED IDEA WITH EVOLUTION
            if (subTab === 'impressed' && idea.latestEvolution) {
              return (
                <div key={idea.id} className="evolution-thread-container" onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}>
                  {/* 1. ORIGINAL IDEA (Compact) */}
                  <div className="thread-original-card">
                    {(idea.thumbUrl || idea.imageUrl) && (
                      <div className="card-thumb-frame">
                        <img src={idea.thumbUrl || idea.imageUrl} alt="" className="thread-original-thumb" loading="lazy" />
                      </div>
                    )}
                    <div className="thread-original-content">
                      <div className="thread-meta-top">
                        <span className="thread-time">{idea.timeAgo}</span>
                        <span className="thread-stage-pill">{idea.stage}</span>
                      </div>
                      <h3 className="thread-original-title">{idea.title}</h3>
                      <div className="thread-tags">
                        {(idea.tags || []).slice(0, 2).map((t, i) => <span key={i} className="thread-tag">{t}</span>)}
                      </div>
                    </div>
                  </div>

                  {/* 2. CONNECTOR VISUAL */}
                  <div className="thread-connector">
                    <div className="connector-line"></div>
                    <div className="connector-icon">
                      {/* You can use a specific icon for the stage of the evolution if avail */}
                      <div className="connector-dot"></div>
                    </div>
                  </div>

                  {/* 3. EVOLUTION CARD */}
                  <div className="thread-evolution-card">
                    <div className="evolution-header">
                      <div className="evolver-avatar">
                        {idea.latestEvolution.evolverImage ?
                          <img src={idea.latestEvolution.evolverImage} alt={idea.latestEvolution.evolverName} />
                          : (idea.latestEvolution.evolverName || "?").charAt(0)}
                      </div>
                      <div className="evolver-info">
                        <span className="evolver-name">{idea.latestEvolution.evolverName}</span>
                        <span className="evolution-label">Evolved into {idea.latestEvolution.stage}</span>
                      </div>
                    </div>
                    <p className="evolution-text">
                      {idea.latestEvolution.text}
                    </p>

                    {/* ACTION BUTTON */}
                    <button className="evolution-action-btn" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/view-idea/${idea.id}`, { state: { idea } });
                    }}>
                      <span className="sparkles-icon">✨</span> EVOLVE
                    </button>
                  </div>
                </div>
              );
            }

            // DEFAULT CARD RENDER (For All, Evolved tabs)
            return (
              <div key={idea.id} className="idea-card-rich" onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}>
                {/* Top Meta */}
                <div className="card-top-meta">
                  <span className="meta-text">{idea.displayAction} {idea.timeAgo}</span>
                  {/* Stage Icon */}
                  <div className="stage-badge">
                    {(idea.stage === 'Orbit' || idea.stage === 'Gravity') && <img src={OrbitIcon} alt="Orbit" className="stage-icon-sm" />}
                    {(idea.stage === 'Nova' || idea.stage === 'Constellation') && <img src={NovaIcon} alt="Nova" className="stage-icon-sm" />}
                    {/* Fallback or other stages */}
                    {!['Orbit', 'Gravity', 'Nova', 'Constellation'].includes(idea.stage) && idea.stage && <span className="stage-text">{idea.stage}</span>}
                  </div>
                </div>

                {/* Row Layout: Thumb + Info */}
                <div className="card-content-row">
                  {/* Thumbnail */}
                  <div className="card-thumb-frame">
                    {(idea.thumbUrl || idea.imageUrl) ?
                      <img src={idea.thumbUrl || idea.imageUrl} alt={idea.title} className="card-thumb" loading="lazy" />
                      : <div className="card-thumb" style={{ background: '#333' }}></div>
                    }
                  </div>

                  {/* Info Column */}
                  <div className="card-info-col">
                    <h3 className="card-rich-title">{idea.title}</h3>

                    {/* Tags */}
                    <div className="card-tags">
                      {(idea.tags || []).slice(0, 3).map((tag, i) => (
                        <span key={i} className="card-tag">{tag}</span>
                      ))}
                    </div>

                    <p className="card-rich-desc">{idea.description}</p>
                  </div>
                </div>

                {/* Evolved Text Overlay for 'Evolved' Tab */}
                {idea._myEvolveText && (subTab === 'evolved' || subTab === 'all') && (
                  <div className="card-evolved-comment-block">
                    <div className="evolved-header">
                      <div className="evolver-info-left">
                        <div className="evolver-avatar">
                          {myProfile?.image ? (
                            <img src={myProfile.image} alt="" />
                          ) : (
                            (myProfile?.name || "U").charAt(0)
                          )}
                        </div>
                        <span className="evolver-name">{myProfile?.name || "You"}</span>
                      </div>
                      <div className="evolver-info-right">
                        <img src={OrbitIcon} alt="" className="tiny-stage-icon" />
                      </div>
                    </div>
                    <div className="evolved-comment-box">
                      <p className="evolved-comment-text">{idea._myEvolveText}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
};

const IdeaSkeleton = () => (
  <div className="idea-card-rich idea--skeleton">
    <div className="skeleton-bar skeleton-dateline" />
    <div className="card-content-row">
      <div className="skeleton-thumb" />
      <div className="card-info-col">
        <div className="skeleton-bar skeleton-title" />
        <div className="skeleton-pill" />
        <div className="skeleton-bar skeleton-desc" />
        <div className="skeleton-bar skeleton-desc-short" />
      </div>
    </div>
  </div>
);

export default DashboardPage;